import React from 'react';
import { Button } from '@/components/Button/ButtonV2';
import { PaymentMethodsStep } from './Steps/PaymentMethodsStep/PaymentMethodsStep';
import { BusinessRoleStep } from './Steps/BusinessRoleStep';
import { TeamCreationStep } from './Steps/TeamCreationStep';
import { WorkingHoursStep } from './Steps/WorkingHoursStep/WorkingHoursStep';
import { FirstServiceStep } from './Steps/FirstServiceStep';
import { ActionType, StepType } from '../context/types';
import { useCustomerJourney } from '../context/setup';
import { motion } from 'framer-motion';
import { trackGenericEvent } from '@/analytics';

export interface StepperActionsProps {
  onCancel?: () => void;
  onSave?: () => void;
  showSaveCancel?: boolean;
  canSave?: boolean;
}

const CustomerJourneyStepper: React.FC = () => {
  const { state, dispatch, canProceedToNextStep, proceedToNextStep, getVisibleSteps, isSaving } =
    useCustomerJourney();

  const visibleSteps = getVisibleSteps();

  const currentStepIndex = visibleSteps.indexOf(state.currentStep);
  const totalSteps = visibleSteps.length;
  const currentStepNumber = currentStepIndex + 1;
  const progressPercentage = Math.round((currentStepNumber / totalSteps) * 100) - 10;
  const isLastStep = state.currentStep === visibleSteps[visibleSteps.length - 1];
  const isFirstServiceStep = state.currentStep === StepType.FIRST_SERVICE;

  const [stepActions, setStepActions] = React.useState<StepperActionsProps>({});

  const handlePreviousStep = () => {
    if (stepActions.showSaveCancel && stepActions.onCancel) {
      stepActions.onCancel();
      return;
    }

    const currentIndex = visibleSteps.indexOf(state.currentStep);
    if (currentIndex > 0) {
      dispatch({
        type: ActionType.SET_STEP,
        payload: visibleSteps[currentIndex - 1]
      });
    }
  };

  const handleNextStep = () => {
    if (stepActions.showSaveCancel && stepActions.onSave) {
      stepActions.onSave();
      return;
    }

    trackGenericEvent('New Journey next step', {
      step: state.currentStep
    });

    proceedToNextStep();
  };

  return (
    <div className="w-full bg-white sm:rounded-xl sm:shadow-lg shadow-blue/5 sm:p-6 min-h-dvh sm:min-h-0 flex flex-col animate-fadeIn">
      <div className="mb-4 md:mb-6 px-4 sm:px-0 pt-5 sm:pt-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <div className="text-sm font-medium text-gray-700">
              Paso {currentStepNumber} de {totalSteps}
            </div>
            <span className="hidden sm:inline text-gray-400">•</span>
            <div className="hidden sm:block text-sm font-medium text-blue">{progressPercentage}%</div>
          </div>
          <div className="block sm:hidden text-sm font-bold text-blue">{progressPercentage}%</div>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-2 md:h-3">
          <motion.div
            className="bg-blue h-2 md:h-3 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progressPercentage}%` }}
            transition={{ duration: 0.5, ease: 'easeInOut' }}
          ></motion.div>
        </div>
      </div>

      <motion.div
        key={state.currentStep}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="flex-1 overflow-auto px-4 sm:px-0 pb-24 sm:pb-0 sm:min-h-[400px] w-full h-full sm:min-w-[600px] sm:max-w-[600px]"
      >
        {state.currentStep === StepType.PAYMENT_METHODS && (
          <PaymentMethodsStep setStepActions={setStepActions} />
        )}
        {state.currentStep === StepType.BUSINESS_ROLE && <BusinessRoleStep />}
        {state.currentStep === StepType.TEAM_CREATION && <TeamCreationStep />}
        {state.currentStep === StepType.WORKING_HOURS && <WorkingHoursStep />}
        {state.currentStep === StepType.FIRST_SERVICE && <FirstServiceStep />}
      </motion.div>

      <div
        className={`fixed sm:relative bottom-0 left-0 right-0 flex justify-between gap-3 pt-4 pb-6 px-4 sm:px-0 sm:pt-4 ${
          isLastStep ? '' : 'sm:border-t border-gray-200'
        } bg-gradient-to-t from-white via-white to-transparent sm:bg-none`}
      >
        <Button
          variant="outline"
          className={`flex-1 !text-base bg-white shadow-sm hover:bg-gray-50 h-14 sm:h-12 rounded-xl ${
            isFirstServiceStep ? 'w-full' : ''
          }`}
          size="lg"
          disabled={!stepActions.showSaveCancel && visibleSteps.indexOf(state.currentStep) === 0}
          onClick={handlePreviousStep}
        >
          {stepActions.showSaveCancel ? 'Cancelar' : 'Anterior'}
        </Button>

        {!isFirstServiceStep && (
          <Button
            className="flex-1 !text-base shadow-md hover:shadow-lg transition-all h-14 sm:h-12 rounded-xl bg-blue hover:bg-blue/90"
            size="lg"
            disabled={
              (stepActions.showSaveCancel && !stepActions.canSave) ||
              (!stepActions.showSaveCancel && !canProceedToNextStep())
            }
            isLoading={isSaving}
            onClick={handleNextStep}
          >
            {stepActions.showSaveCancel ? 'Guardar' : isLastStep ? 'Finalizar' : 'Siguiente'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default CustomerJourneyStepper;
